import { useEffect, useState } from 'react';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import type { MediaAgentRecoveryV1 } from '@/types/player/perform-control-v1.types.ts';
import { isMediaAgentRecoveryEngaged, resolveMediaAgentRecoveryShowAtMs } from '@/helpers/media-agent-recovery.helper';

/**
 * `live-context-perform-control` overlay ∪ GET mirror for the media-agent outage episode.
 * The WS value always wins — it also carries the `active: false` clear, which a stale GET may not.
 */
export function useMediaAgentRecovery(
  entityRef: string | undefined,
  recoveryFromGet: MediaAgentRecoveryV1 | undefined,
): MediaAgentRecoveryV1 | undefined {
  const roomKey = entityRef ? entityRoomKeyFromEntityRef(entityRef) : null;
  const fromWs = usePerformControlWsStore((s) => (roomKey ? s.mediaAgentRecoveryByRoom[roomKey] : undefined));
  return fromWs ?? recoveryFromGet;
}

/**
 * Gates recovery UI behind the service-side UI delay measured from `since`, so a brief blip that
 * clears within the window never reaches the operator. Hides immediately once the episode clears.
 */
export function useMediaAgentRecoveryVisible(recovery: MediaAgentRecoveryV1 | undefined): boolean {
  const engaged = isMediaAgentRecoveryEngaged(recovery);
  const since = recovery?.since;
  const [delayElapsed, setDelayElapsed] = useState(false);

  useEffect(() => {
    if (!engaged || !recovery) {
      setDelayElapsed(false);
      return;
    }
    const now = Date.now();
    const remainingMs = resolveMediaAgentRecoveryShowAtMs(recovery, now) - now;
    if (remainingMs <= 0) {
      setDelayElapsed(true);
      return;
    }
    setDelayElapsed(false);
    const timeoutId = window.setTimeout(() => setDelayElapsed(true), remainingMs);
    return () => window.clearTimeout(timeoutId);
    // `since` identifies the episode; phase transitions must not restart the delay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engaged, since]);

  return engaged && delayElapsed;
}
